import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, AudioLines, Sparkles } from "lucide-react";
import { fadeUp } from "@/lib/motion";
import { genreColor } from "@/lib/genreColors";

function SectionHead({ kicker, title, live, accent }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div>
        <p
          className="font-rhythm-mono text-[11px] uppercase tracking-[0.22em] text-[#F3F1ED]"
          style={accent ? { color: accent } : undefined}
        >
          {kicker}
        </p>
        <h2 className="mt-3 font-rhythm-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl">
          {title}
        </h2>
      </div>
      {live && (
        <span className="inline-flex items-center gap-2 rounded-full border border-[#F3F1ED]/40 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#F3F1ED]">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#F3F1ED] opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#F3F1ED]" />
          </span>
          Live
        </span>
      )}
    </div>
  );
}

export default function RhythmSections({ items, tops }) {
  const friday = items.filter((i) => i.kind === "friday");
  const spotlights = items.filter((i) => i.kind === "spotlight");

  return (
    <>
      <section id="friday" className="mx-auto max-w-7xl px-5 pt-16 sm:pt-24">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }}>
          <SectionHead kicker="Every Friday" title="New Music Friday" live accent="#FF2A6D" />
        </motion.div>
        {friday.length === 0 ? (
          <p data-testid="rhythm-friday-empty" className="mt-8 font-garamond text-xl italic text-white/60">
            The next drop lands Friday. Bring your headphones.
          </p>
        ) : (
          <>
            {tops?.["Billboard Hot 100"]?.length > 0 && (
              <div data-testid="rhythm-hot100-board" className="mt-9 border border-white/15">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-[#F3F1ED] px-5 py-3.5">
                  <p className="font-rhythm-mono text-[10px] uppercase tracking-[0.22em] text-[#F3F1ED]">
                    The Board — Billboard Hot 100
                  </p>
                  <p className="font-rhythm-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
                    This week · Live
                  </p>
                </div>
                <div className="grid gap-x-10 px-5 sm:grid-cols-2">
                  {tops["Billboard Hot 100"].slice(0, 10).map((s, i) => (
                    <div
                      key={`${s.title}-${s.artist}-${i}`}
                      data-testid={`rhythm-hot100-row-${i}`}
                      className="flex min-w-0 items-baseline gap-4 border-b border-white/10 py-2.5"
                    >
                      <span className="w-6 shrink-0 text-right font-rhythm-display text-base text-[#F3F1ED]">
                        {s.position}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-white/90">{s.title}</span>
                        <span className="block truncate font-rhythm-mono text-[9px] uppercase tracking-[0.16em] text-white/45">
                          {s.artist}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
                <p className="px-5 py-3 font-rhythm-mono text-[9px] uppercase tracking-[0.18em] text-white/35">
                  Live from the chart pipeline — refreshes hourly
                </p>
              </div>
            )}
            <div className="mt-9 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {friday.map((f, idx) => (
              <motion.article
                key={f.slug}
                data-testid={`rhythm-friday-card-${idx}`}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.15 }}
                className="rhythm-card group overflow-hidden rounded-3xl"
              >
                <div className="overflow-hidden">
                  <img
                    src={f.image_url}
                    alt={`${f.artist} — ${f.title}`}
                    className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between gap-3">
                    <span
                      className="rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
                      style={(() => {
                        const gc = genreColor(f.genre);
                        return gc ? { borderColor: gc.hex, color: gc.hex, background: gc.bg } : undefined;
                      })()}
                    >
                      {f.ptype} · {f.genre}
                    </span>
                    <span className="font-rhythm-mono text-[10px] uppercase tracking-[0.14em] text-[#F3F1ED]">
                      New
                    </span>
                  </div>
                  <h3 className="mt-4 font-rhythm-display text-lg font-bold uppercase tracking-snug">
                    {f.artist}
                  </h3>
                  <p className="font-garamond text-lg italic text-white/85">{f.title}</p>
                  <p className="mt-3 text-sm leading-relaxed text-white/65">{f.excerpt}</p>
                  <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">
                    {f.year} · {f.week}
                  </p>
                </div>
              </motion.article>
            ))}
            </div>
            </>
          )}
      </section>

      <section id="spotlights" className="mx-auto max-w-7xl px-5 pt-16 sm:pt-24">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }}>
          <SectionHead kicker="Artists to watch" title="Artist Spotlights" accent="#C084FC" />
        </motion.div>
        {spotlights.length === 0 ? (
          <p data-testid="rhythm-spotlight-empty" className="mt-8 font-garamond text-xl italic text-white/60">
            The next artist worth your playlist is being decided.
          </p>
        ) : (
          <div className="mt-9 flex snap-x gap-6 overflow-x-auto pb-4">
            {spotlights.map((sp, idx) => {
              const card = (
                <motion.article
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.15 }}
                  className={`rhythm-card group relative h-full overflow-hidden rounded-3xl ${sp.body ? "" : "min-w-[300px] snap-start sm:min-w-[360px]"}`}
                >
                  <div className="relative">
                    <img src={sp.image_url} alt={sp.artist} className="h-56 w-full object-cover grayscale transition-all duration-500 group-hover:scale-[1.03] group-hover:grayscale-0" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0A0F] via-[#0B0A0F]/20 to-transparent" />
                    <span className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full border border-[#F3F1ED]/50 bg-[#0B0A0F]/70 px-3 py-1 font-rhythm-mono text-[10px] uppercase tracking-[0.14em] text-[#F3F1ED] backdrop-blur">
                      <Sparkles className="h-3 w-3" /> {sp.week}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-rhythm-display text-xl font-bold uppercase tracking-snug">{sp.artist}</h3>
                    <span className="mt-2 inline-block rounded-full border border-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white/70">
                      {sp.genre}
                    </span>
                    <p className="mt-4 text-sm leading-relaxed text-white/65">{sp.excerpt}</p>
                    {sp.body && (
                      <span className="mt-5 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F3F1ED]">
                        Read the spotlight <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    )}
                  </div>
                </motion.article>
              );
              return sp.body ? (
                <Link
                  key={sp.slug}
                  data-testid={`rhythm-spotlight-card-${idx}`}
                  to={`/rhythm/${sp.slug}`}
                  className="min-w-[300px] snap-start sm:min-w-[360px]"
                >
                  {card}
                </Link>
              ) : (
                <div key={sp.slug} data-testid={`rhythm-spotlight-card-${idx}`} className="min-w-[300px] snap-start sm:min-w-[360px]">
                  {card}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}
